import { useState, useMemo } from 'react';
import { ToolLayout } from '../components/Layout/ToolLayout';

interface JWTPayload {
  header: Record<string, unknown>;
  payload: Record<string, unknown>;
  signature: string;
}

export function JWTDecoder() {
  const [input, setInput] = useState('');
  const [error, setError] = useState<string | null>(null);

  const decoded = useMemo<JWTPayload | null>(() => {
    if (!input.trim()) {
      setError(null);
      return null;
    }

    try {
      const parts = input.trim().split('.');
      
      if (parts.length !== 3) {
        setError('Invalid JWT format. A JWT must have 3 parts separated by dots.');
        return null;
      }

      const [headerB64, payloadB64, signature] = parts;

      // Decode Base64URL to Base64
      const base64UrlToBase64 = (str: string) => {
        let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
        const pad = base64.length % 4;
        if (pad) {
          base64 += '='.repeat(4 - pad);
        }
        return base64;
      };

      const header = JSON.parse(atob(base64UrlToBase64(headerB64)));
      const payload = JSON.parse(atob(base64UrlToBase64(payloadB64)));

      setError(null);
      return { header, payload, signature };
    } catch (err) {
      setError('Failed to decode JWT. Please check the format.');
      return null;
    }
  }, [input]);

  const formatTimestamp = (timestamp: number): string => {
    try {
      return new Date(timestamp * 1000).toLocaleString();
    } catch {
      return String(timestamp);
    }
  };

  const renderOutput = () => {
    if (error) {
      return (
        <div className="text-red-400">
          <span className="font-semibold">Error:</span> {error}
        </div>
      );
    }

    if (!decoded) {
      return null;
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-2 flex items-center gap-2">
            <span className="text-[var(--text-muted)]">{'{'}</span>
            Header
            <span className="text-xs text-[var(--text-muted)]">ALGORITHM & TOKEN TYPE</span>
          </h3>
          <pre className="text-sm whitespace-pre-wrap break-all">
            {JSON.stringify(decoded.header, null, 2)}
          </pre>
        </div>

        {/* Payload */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-2 flex items-center gap-2">
            <span className="text-[var(--text-muted)]">{'{'}</span>
            Payload
            <span className="text-xs text-[var(--text-muted)]">DATA</span>
          </h3>
          <pre className="text-sm whitespace-pre-wrap break-all">
            {JSON.stringify(decoded.payload, null, 2)}
          </pre>

          {/* Common claims info */}
          <div className="mt-4 space-y-2 text-sm">
            {typeof decoded.payload.exp === 'number' && (
              <div className="flex gap-2">
                <span className="text-[var(--text-muted)]">Expires:</span>
                <span className={
                  decoded.payload.exp * 1000 < Date.now()
                    ? 'text-red-400'
                    : 'text-[var(--accent)]'
                }>
                  {formatTimestamp(decoded.payload.exp)}
                  {decoded.payload.exp * 1000 < Date.now() && ' (EXPIRED)'}
                </span>
              </div>
            )}
            {typeof decoded.payload.iat === 'number' && (
              <div className="flex gap-2">
                <span className="text-[var(--text-muted)]">Issued at:</span>
                <span>{formatTimestamp(decoded.payload.iat)}</span>
              </div>
            )}
            {typeof decoded.payload.nbf === 'number' && (
              <div className="flex gap-2">
                <span className="text-[var(--text-muted)]">Not before:</span>
                <span>{formatTimestamp(decoded.payload.nbf)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Signature */}
        <div>
          <h3 className="text-[var(--accent)] font-semibold mb-2 flex items-center gap-2">
            <span className="text-[var(--text-muted)]">{'{'}</span>
            Signature
            <span className="text-xs text-[var(--text-muted)]">VERIFY SIGNATURE</span>
          </h3>
          <pre className="text-sm text-[var(--text-muted)] whitespace-pre-wrap break-all">
            {decoded.signature}
          </pre>
          <p className="mt-2 text-xs text-[var(--text-muted)]">
            Note: Signature verification requires the secret key and is not performed client-side.
          </p>
        </div>
      </div>
    );
  };

  return (
    <ToolLayout
      title="JWT Decoder"
      description="Decode and inspect JSON Web Tokens"
      inputLabel="JWT Token"
      inputPlaceholder="Enter your JWT token here (e.g., eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...)"
      inputValue={input}
      onInputChange={setInput}
      outputLabel="Decoded Token"
      output={renderOutput()}
      privacyNote={true}
    />
  );
}
